import { useEffect, useRef, useState } from "react";
import { AuthCard } from "@/components/auth/AuthCard";
import { MFAVerification } from "@/components/auth/MFAVerification";
import { Button } from "@/components/ui/button";
import {
  clearAdminPasskeyProof,
  verifyAdminPasskey,
} from "@/lib/adminPasskeys";

export function AdminPasskeyVerification({
  factorId,
  onSuccess,
  onCancel,
  compact = false,
}: {
  factorId?: string | null;
  compact?: boolean;
  onSuccess: () => void;
  onCancel: () => void | Promise<void>;
}) {
  const [totp, setTotp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  const cancel = () => {
    pending.current?.abort();
    clearAdminPasskeyProof();
    void onCancel();
  };
  if (totp && factorId)
    return (
      <MFAVerification
        factorId={factorId}
        onSuccess={onSuccess}
        onCancel={cancel}
      />
    );
  const verify = async () => {
    const controller = new AbortController();
    pending.current = controller;
    setBusy(true);
    setError(null);
    try {
      await verifyAdminPasskey(controller.signal);
      if (!controller.signal.aborted) onSuccess();
    } catch (e) {
      if (!controller.signal.aborted)
        setError(
          e instanceof Error && e.name === "NotAllowedError"
            ? "Passkey verification was cancelled. Try again or use your authenticator app."
            : e instanceof Error
              ? e.message
              : "Unable to verify passkey.",
        );
    } finally {
      if (!controller.signal.aborted) setBusy(false);
    }
  };
  return (
    <div
      className={
        compact
          ? "flex items-center justify-center"
          : "flex min-h-screen items-center justify-center bg-background p-6"
      }
    >
      <AuthCard
        title="Verify your admin sign in"
        description="Use a passkey to complete two-factor authentication for the admin console."
        contentClassName="space-y-4"
        data-testid="admin-passkey-verification"
      >
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <Button
          className="w-full"
          disabled={busy}
          onClick={() => void verify()}
        >
          {busy ? "Waiting for passkey…" : "Use passkey"}
        </Button>
        {factorId && (
          <Button
            className="w-full"
            variant="outline"
            disabled={busy}
            onClick={() => setTotp(true)}
          >
            Use authenticator app
          </Button>
        )}
        <Button className="w-full" variant="ghost" onClick={cancel}>
          Cancel and sign out
        </Button>
      </AuthCard>
    </div>
  );
}
